import React, { useState, useEffect, useRef, useCallback, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import ClassSectionFilter from './parts/ClassSectionFilter';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ViewStudentModal from './parts/ViewStudentModal';
import RegisterStudentModal from './parts/RegisterStudentModal';
import BulkEditStudentsModal from './parts/BulkEditStudentsModal';
import BulkEditPreModal from './parts/BulkEditPreModal';
import PasswordResetModal from './parts/PasswordResetModal';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import SwapProfilePictureModal from './parts/SwapProfilePictureModal';
import ExportStudentsZip from './parts/ExportStudentsZip'; // ✅ NEW

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface StudentData {
  class: string;
  id: number;
  user_id: number;
  name: string;
  class_id: string;
  class_name: string;
  section: string | null;
  gender: string;
  email_id: string;
  admission_no: string;
  admission_type: string;
  roll_no: number;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  profile_picture_url: string;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_items: number;
  per_page: number;
}

const StudentsListDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showAllSections, setShowAllSections] = useState<boolean>(false);
  const [studentsPage, setStudentsPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 10,
  });

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentData | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordResetTarget, setPasswordResetTarget] = useState<StudentData | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectedIdsRef = useRef<Set<number>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isPreModalOpen, setIsPreModalOpen] = useState(false);
  const [bulkVisibleFields, setBulkVisibleFields] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const allOnPageChecked = students.length > 0 && students.every((s) => selectedIds.has(s.id));

  const selectedStudents = students
    .filter((s) => selectedIds.has(s.id))
    .map((s) => ({
      ...s,
      section: s.section ?? '',
      class_name: s.class_name || String(s.class) || '',
      class_id: s.class_id || String(s.class) || '',
    }));

  const swapStudentA = selectedIds.size === 2 ? students.find((s) => s.id === Array.from(selectedIds)[0]) ?? null : null;
  const swapStudentB = selectedIds.size === 2 ? students.find((s) => s.id === Array.from(selectedIds)[1]) ?? null : null;

  const clearSelection = useCallback(() => {
    const empty = new Set<number>();
    selectedIdsRef.current = empty;
    setSelectedIds(empty);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [studentsPage, selectedClass, selectedSection, showAllSections, debouncedSearch, pageSize]);

  useEffect(() => {
    fetchStudents();
  }, [studentsPage, selectedClass, selectedSection, showAllSections, debouncedSearch, pageSize]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => setClasses(loadedClasses);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const cls = classes.find((c) => c.id === selectedClass);
    return cls ? Boolean(cls.sections?.length) : false;
  };

  const shouldFetchStudents = (): boolean => {
    if (debouncedSearch.trim().length > 0) return true;
    if (!selectedClass) return false;
    if (selectedClassHasSections()) return selectedSection !== null || showAllSections;
    return true;
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setStudentsPage(1);
    }, 400);
  };

  const handleShowAllSections = () => {
    setShowAllSections(true);
    setStudentsPage(1);
  };

  const fetchStudents = async (): Promise<void> => {
    if (!shouldFetchStudents()) {
      setStudents([]);
      return;
    }
    setLoading(true);
    try {
      const params: any = { page: studentsPage, limit: pageSize };
      if (selectedClass) params.class_id = selectedClass;
      if (selectedSection && !showAllSections) params.section = selectedSection;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const response = await apiClient.get('/get-students-list/class-section', { params });
      const data = response.data;

      if (data.status === 'success') {
        setStudents(data.data);
        setPaginationData({
          current_page: data.pagination.current_page,
          total_pages: data.pagination.total_pages,
          total_items: data.pagination.total_items,
          per_page: data.pagination.per_page,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch students',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching students',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReorderRollNumbers = async () => {
    if (!selectedClass) return;
    const classData = classes.find((c) => c.id === selectedClass);
    if (!classData) return;
    try {
      setLoading(true);
      const payload: any = { class_name: classData.class_name };
      if (selectedSection) payload.section = selectedSection;
      const response = await apiClient.post('/reorder-roll-numbers-by-name', payload);
      const data = response.data;
      if (data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Roll numbers reordered!',
          life: 4000,
        });
        fetchStudents();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: 'Failed to reorder roll numbers!',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) return;
    try {
      const response = await apiClient.post('/delete-student-by-user-id', { user_id: userId });
      const data = response.data;
      if (data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Student deleted successfully!',
          life: 4000,
        });
        fetchStudents();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: 'Failed to delete student!',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    }
  };

  const handlePasswordReset = async (newPassword: string) => {
    await apiClient.post('/admin/reset-password', {
      user_id: passwordResetTarget?.user_id,
      new_password: newPassword,
    });
  };

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
    setSelectedSection(null);
    setShowAllSections(false);
    setStudentsPage(1);
    clearSelection();
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
    setShowAllSections(false);
    setStudentsPage(1);
    clearSelection();
  };

  const toggleSelectionMode = () => {
    const next = !selectionMode;
    setSelectionMode(next);
    clearSelection();
    setTableKey((k) => k + 1);
  };

  const toggleOne = useCallback(
    (id: number, checkboxEl?: HTMLInputElement) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          if (checkboxEl) checkboxEl.checked = false;
        } else {
          next.add(id);
          if (checkboxEl) checkboxEl.checked = true;
        }
        selectedIdsRef.current = next;
        return next;
      });
    },
    []
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        students.forEach((s) => (checked ? next.add(s.id) : next.delete(s.id)));
        selectedIdsRef.current = next;
        return next;
      });
    },
    [students]
  );

  const handlePreModalConfirm = (fields: string[]) => {
    setBulkVisibleFields(fields);
    setIsBulkEditOpen(true);
  };

  const handleBulkEditSuccess = () => {
    setIsBulkEditOpen(false);
    setBulkVisibleFields([]);
    clearSelection();
    setSelectionMode(false);
    setTableKey((k) => k + 1);
    fetchStudents();
  };

  const handleSwapSuccess = () => {
    clearSelection();
    setSelectionMode(false);
    setTableKey((k) => k + 1);
    fetchStudents();
  };

  const calculateAge = (dob: string): string => {
    const b = new Date(dob),
      t = new Date();
    let age = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
    return `${age} yr`;
  };

  const getEmptyStateContent = () => {
    if (debouncedSearch.trim())
      return {
        icon: 'pi pi-search',
        title: 'No Results Found',
        message: `No students matched "${debouncedSearch}".`,
      };
    if (!selectedClass)
      return {
        icon: 'pi pi-filter',
        title: 'No Class Selected',
        message: 'Please select a class from the filter above to view students',
      };
    if (selectedClassHasSections() && !selectedSection && !showAllSections)
      return {
        icon: 'pi pi-list',
        title: 'No Section Selected',
        message: 'Please select a section, or tap "All Sections" to view all students in this class',
      };
    return {
      icon: 'pi pi-inbox',
      title: 'No Students Found',
      message: 'There are no students enrolled in this class/section yet',
    };
  };

  const checkboxHeader = (): JSX.Element => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={allOnPageChecked}
        onChange={(e) => toggleAll(e.target.checked)}
        style={cbStyle}
      />
    </div>
  );

  const checkboxTemplate = (rowData: StudentData): JSX.Element => {
    const isChecked = selectedIds.has(rowData.id);
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <input
          type="checkbox"
          defaultChecked={isChecked}
          key={`cb-${rowData.id}-${isChecked}`}
          onChange={(e) => toggleOne(rowData.id, e.target)}
          onClick={(e) => e.stopPropagation()}
          style={cbStyle}
        />
      </div>
    );
  };

  const serialNumberTemplate = (_: StudentData, options: ColumnBodyOptions): JSX.Element => {
    const index = options.rowIndex + (studentsPage - 1) * pageSize;
    return (
      <div
        style={{
          width: 28,
          height: 28,
          background: '#f9fafb',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          color: '#6b7280',
          margin: '0 auto',
        }}
      >
        {index + 1}
      </div>
    );
  };

  const studentNameTemplate = (rowData: StudentData): JSX.Element => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ProfilePictureHover
          src={rowData.profile_picture_url}
          name={rowData.name}
          cacheBust={new Date(rowData.updated_at).getTime().toString()}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: '#1f2937',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {rowData.name}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {calculateAge(rowData.dob)}
          </div>
        </div>
      </div>
    );
  };

  const genderTemplate = (rowData: StudentData): JSX.Element => (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: rowData.gender?.toLowerCase() === 'male' ? '#3b82f6' : '#ec4899',
        textTransform: 'capitalize',
      }}
    >
      {rowData.gender || '—'}
    </div>
  );

  const fatherNameTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>{r.father_name}</div>
  );

  const contactTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>{r.father_no}</div>
  );

  const rollNoTemplate = (r: StudentData) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        padding: '4px 8px',
        borderRadius: 6,
        background: '#f9fafb',
        fontSize: 12,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
      }}
    >
      {r.roll_no}
    </div>
  );

  const admissionNoTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>{r.admission_no}</div>
  );

  const admissionTypeTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>
      {r.admission_type?.trim() || 'N/A'}
    </div>
  );

  const classTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{r.class_name}</div>
  );

  const sectionTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: r.section ? '#1f2937' : '#9ca3af', fontWeight: r.section ? 600 : 400 }}>
      {r.section && r.section.trim() ? r.section : '—'}
    </div>
  );

  const dobTemplate = (r: StudentData) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{new Date(r.dob).toLocaleDateString()}</div>
  );

  const addressTemplate = (r: StudentData) => (
    <div
      style={{
        fontSize: 13,
        color: r.address?.trim() ? '#1f2937' : '#9ca3af',
        fontWeight: 400,
        maxWidth: 180,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={r.address?.trim() || '—'}
    >
      {r.address?.trim() || '—'}
    </div>
  );

  const actionTemplate = (rowData: StudentData) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-rounded"
        tooltip="View"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#60a5fa', width: 32, height: 32 }}
        onClick={() => {
          setSelectedStudentId(rowData.id.toString());
          setIsViewModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => {
          setEditingStudent(rowData);
          setIsRegisterModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-key"
        className="p-button-text p-button-rounded"
        tooltip="Reset Password"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#8b5cf6', width: 32, height: 32 }}
        onClick={() => {
          setPasswordResetTarget(rowData);
          setIsPasswordModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => handleDeleteStudent(rowData.user_id)}
      />
    </div>
  );

  const emptyState = getEmptyStateContent();

  // ✅ Students passed to export — uses current page. If you want ALL pages,
  // you'd fetch with limit=total_items separately. For now, current page is exported.
  const exportStudents = students;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn   { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px)  } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-datatable .p-datatable-tbody > tr.row-selected { background:rgba(59,130,246,.1)!important; box-shadow:inset 4px 0 0 0 #3b82f6!important; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:16px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .search-input-wrapper { display:flex; align-items:center; gap:10px; background:white; border-radius:16px; padding:12px 18px; margin-bottom:20px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); transition:border-color .2s,box-shadow .2s; }
          .search-input-wrapper:focus-within { border-color:rgba(59,130,246,.4); box-shadow:0 2px 12px rgba(30,64,175,.14); }
          .search-input-wrapper input[type="text"] { flex:1; border:none; outline:none; font-size:13px; font-weight:500; color:#1f2937; background:transparent; }
          .search-input-wrapper input[type="text"]::placeholder { color:#9ca3af; font-weight:400; }
          .search-clear-btn { background:none; border:none; cursor:pointer; color:#9ca3af; padding:2px 4px; border-radius:4px; display:flex; align-items:center; transition:color .2s; }
          .search-clear-btn:hover { color:#6b7280; }
          .bulk-action-bar { animation:bulkBarIn .22s ease; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:14px; padding:12px 18px; margin-bottom:16px; box-shadow:0 4px 16px rgba(59,130,246,.3); }
          .bulk-count { font-size:13px; font-weight:700; color:white; }
          .bulk-count span { opacity:.75; font-weight:500; }
          .bulk-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
          .bulk-action-btn.primary { background:white; color:#1e40af; }
          .bulk-action-btn.primary:hover { background:#eff6ff; }
          .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
          .bulk-action-btn.ghost:hover { background:rgba(255,255,255,.25); }
          .bulk-action-btn.swap { background:linear-gradient(135deg,#f59e0b,#d97706); color:white; box-shadow:0 2px 8px rgba(245,158,11,.35); }
          .bulk-action-btn.swap:hover { background:linear-gradient(135deg,#fbbf24,#f59e0b); }
          .bulk-action-btn.swap:disabled { opacity:.45; cursor:not-allowed; }
          .select-mode-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; transition:all .2s; border:1.5px solid; }
          .select-mode-btn.off { background:white; color:#4169E1; border-color:#c7d2fe; }
          .select-mode-btn.off:hover { background:#eff6ff; }
          .select-mode-btn.on { background:#4169E1; color:white; border-color:#4169E1; }
        `}</style>

        <GlobalHeaderTitle
          title="Students Directory"
          subtitle={`${paginationData.total_items} total students`}
          icon="pi pi-users"
          buttons={[
            {
              label: 'Reorder Roll No',
              icon: 'pi pi-sort-alpha-down',
              onClick: handleReorderRollNumbers,
              outlined: true,
              disabled: !selectedClass || loading,
              style: { borderWidth: '2px' },
            },
            {
              label: 'Add Student',
              icon: 'pi pi-plus',
              onClick: () => {
                setEditingStudent(null);
                setIsRegisterModalOpen(true);
              },
            },
          ]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
          allowAllSections
          showingAllSections={showAllSections}
          onShowAllSections={handleShowAllSections}
        />

        <div className="search-input-wrapper">
          <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search by name, admission no, father name, or roll no..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear-btn" onClick={() => handleSearchChange('')}>
              <i className="pi pi-times" style={{ fontSize: 12 }} />
            </button>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="bulk-action-bar">
            <div className="bulk-count">
              {selectedIds.size} student{selectedIds.size !== 1 ? 's' : ''} selected{' '}
              <span>of {students.length} on this page</span>
              {selectedIds.size === 2 && (
                <span
                  style={{
                    marginLeft: 10,
                    padding: '2px 8px',
                    borderRadius: 12,
                    background: 'rgba(245,158,11,.25)',
                    color: '#fef3c7',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  💡 Swap photos available
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="bulk-action-btn primary" onClick={() => setIsPreModalOpen(true)}>
                <i className="pi pi-pencil" style={{ fontSize: 12 }} /> Bulk Edit
              </button>

              {selectedIds.size === 2 && (
                <button
                  className="bulk-action-btn swap"
                  onClick={() => setIsSwapModalOpen(true)}
                  title={`Swap profile pictures between ${swapStudentA?.name} and ${swapStudentB?.name}`}
                >
                  <i className="pi pi-sync" style={{ fontSize: 12 }} /> Swap Photos
                </button>
              )}

              <button className="bulk-action-btn ghost" onClick={clearSelection}>
                <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear Selection
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {students.length > 0 && (
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: '20px 20px 0 0',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                    Showing {((studentsPage - 1) * pageSize) + 1}–
                    {Math.min(studentsPage * pageSize, paginationData.total_items)} of{' '}
                    {paginationData.total_items} students
                    {debouncedSearch.trim() && <span style={{ color: '#3b82f6' }}> for "{debouncedSearch}"</span>}
                    {showAllSections && !debouncedSearch.trim() && (
                      <span style={{ color: '#10b981' }}> (all sections)</span>
                    )}
                  </span>
                </div>

                {/* ✅ RIGHT SIDE: Select toggle + Export ZIP + Page indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className={`select-mode-btn ${selectionMode ? 'on' : 'off'}`} onClick={toggleSelectionMode}>
                    <i className="pi pi-check-square" style={{ fontSize: 12 }} />
                    {selectionMode ? 'Exit Select' : 'Select'}
                  </button>

                  {/* ✅ Export ZIP button lives here, passing current page students */}
                  <ExportStudentsZip
                    students={exportStudents}
                    label="Export ZIP"
                    disabled={students.length === 0}
                  />

                  <div
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                      border: '1px solid rgba(65,105,225,.15)',
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                      Page {paginationData.current_page} of {paginationData.total_pages}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <DataTable
              key={tableKey}
              value={students}
              loading={false}
              rowClassName={(rowData: StudentData) => (selectedIds.has(rowData.id) ? 'row-selected' : '')}
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 400,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      color: '#1e40af',
                      fontSize: 20,
                      fontWeight: 700,
                      marginBottom: 8,
                      textAlign: 'center',
                    }}
                  >
                    {emptyState.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {emptyState.message}
                  </p>
                </div>
              }
            >
              {selectionMode && (
                <Column header={checkboxHeader} body={checkboxTemplate} style={{ width: 52, textAlign: 'center' }} />
              )}
              <Column header="#" body={serialNumberTemplate} style={{ width: 52 }} />
              <Column header="Student Name" body={studentNameTemplate} />
              <Column header="Gender" body={genderTemplate} style={{ width: 90 }} />
              <Column header="Father Name" body={fatherNameTemplate} />
              <Column header="Contact" body={contactTemplate} />
              <Column header="Roll No" body={rollNoTemplate} style={{ width: 80 }} />
              <Column header="Admission No" body={admissionNoTemplate} />
              <Column header="Admission Type" body={admissionTypeTemplate} />
              <Column header="Class" body={classTemplate} style={{ width: 80 }} />
              <Column header="Section" body={sectionTemplate} style={{ width: 90 }} />
              <Column header="DOB" body={dobTemplate} />
              <Column header="Address" body={addressTemplate} style={{ width: 200 }} />
              <Column header="Actions" body={actionTemplate} style={{ width: 140 }} />
            </DataTable>

            {paginationData.total_pages > 1 && (
              <CustomPaginator
                page={paginationData.current_page}
                totalPages={paginationData.total_pages}
                totalRecords={paginationData.total_items}
                pageSize={pageSize}
                onPageChange={(newPage) => setStudentsPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setStudentsPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      <ViewStudentModal open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} studentId={selectedStudentId} />

      <RegisterStudentModal
        open={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={(shouldResetPage = false) => {
          setIsRegisterModalOpen(false);
          setEditingStudent(null);
          if (shouldResetPage) setStudentsPage(1);
          fetchStudents();
        }}
        studentData={editingStudent}
      />

      <BulkEditPreModal
        open={isPreModalOpen}
        onClose={() => setIsPreModalOpen(false)}
        studentCount={selectedIds.size}
        onConfirm={handlePreModalConfirm}
      />

      <BulkEditStudentsModal
        open={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        onSuccess={handleBulkEditSuccess}
        students={selectedStudents}
        visibleFields={bulkVisibleFields}
      />

      <PasswordResetModal
        visible={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPasswordResetTarget(null);
        }}
        onSubmit={handlePasswordReset}
        studentName={passwordResetTarget?.name ?? ''}
      />

      <SwapProfilePictureModal
        open={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        onSuccess={handleSwapSuccess}
        studentA={swapStudentA ?? null}
        studentB={swapStudentB ?? null}
      />
    </>
  );
};

const cbStyle: React.CSSProperties = {
  width: 17,
  height: 17,
  cursor: 'pointer',
  accentColor: '#3b82f6',
};

export default StudentsListDashboard;